import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { clsx } from 'clsx'

import {
  hasActionPermission,
  hasExplicitInputPermission,
} from '@tribeplatform/gql-client/lib'
import {
  CustomFieldSchema,
  CustomFieldType,
  PollEntityType,
  Post,
  PostType,
  PostTypeContext,
  Space,
} from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Container } from '@tribeplatform/react-ui-kit/Layout'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { logger } from '../common/lib/logger.js'
import { convertUtcToTz, formatUtcDateTime } from '../common/utils/date.js'
import { FieldInput } from '../CustomField/FieldInput.js'
import { FieldPrivacyBadges } from '../CustomField/FieldPrivacyBadges.js'
import { CustomFieldSubtype } from '../CustomField/index.js'
import { getFieldSetting, useFieldPermissions } from '../CustomField/utils.js'
import { FormDateTime } from '../Form/FormDateTime/FormDateTime.js'
import { Form, FormRef } from '../Form/index.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { getTranslatedPostTypeName } from '../Post/utils/postTranslate.js'
import { Prompt } from '../Prompt/index.js'

import { PostFormActions } from './Components/PostFormActions.js'
import { PostSeoOptions } from './PostSeoOptions.js'
import { SimilarPosts } from './SimilarPosts.js'
import { PostFormFields } from './types.js'
import { useSubmitPost } from './useSubmitPost.js'

export function AdminOnlyInfo() {
  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <SvgIcon name="eye-off" inline />
      </Tooltip.Trigger>
      <Tooltip.Panel>
        <T
          id="Post.AdminOnlyInfo"
          defaultMessage="Only admins and moderators can see and update this section."
        />
      </Tooltip.Panel>
    </Tooltip>
  )
}

export function PostForm({
  space,
  previewFields,
  post,
  postType,
  onCancel,
  availableSpaces,
  onSubmit: onFormSubmit,
  preview = false,
  highlightedField,
  isPostReady,
}: {
  space?: Space
  previewFields?: CustomFieldSchema[]
  availableSpaces?: Space[]
  post?: Post
  postType: PostType
  onCancel?: () => void
  onSubmit?: (post: Post) => void
  preview?: boolean
  highlightedField?: string
  isPostReady: boolean
}) {
  const intl = useI18n()
  const { $t } = intl
  const formRef = useRef<FormRef>()
  const isFormReady = useRef(false)

  const isNew = !post?.id
  const fields = previewFields ?? postType?.postFields?.fields
  const similarQueryKey = 'title'

  const decisionSimilarPosts = false

  const { data: member } = useAuthMember()
  const { canUpdateField } = useFieldPermissions(space)

  const [enablePrompt, setEnablePrompt] = useState(false)
  const [similarQuery, setSimilarQuery] = useState(null)
  const [targetSpace, setTargetSpace] = useState(space)

  const isCreatingPost = !post
  const permissions = isCreatingPost
    ? space?.authMemberProps?.permissions
    : post?.authMemberProps?.permissions
  const { actionPermission } = hasActionPermission(
    permissions ?? [],
    isCreatingPost ? 'createPost' : 'updatePost',
  )

  const { authorized: canLock } = hasExplicitInputPermission(
    actionPermission?.inputPermissions,
    'input.locked',
  )
  const { authorized: canSetOwnerId } = hasExplicitInputPermission(
    actionPermission?.inputPermissions,
    'input.ownerId',
  )
  const { authorized: canSetPublishedAt } = hasExplicitInputPermission(
    actionPermission?.inputPermissions,
    'input.publishedAt',
  )
  const { authorized: canSetSlug } = hasExplicitInputPermission(
    actionPermission?.inputPermissions,
    'input.slug',
  )
  const { authorized: canSetCustomSeoDetail } = hasExplicitInputPermission(
    actionPermission?.inputPermissions,
    'input.customSeoDetail',
  )
  const { authorized: canSetPollIds } = hasExplicitInputPermission(
    actionPermission?.inputPermissions,
    'input.pollIds',
  )

  useEffect(() => {
    setTargetSpace(space)
  }, [space])

  const { onSubmit, isAdding, isUpdating } = useSubmitPost({
    canSetOwnerId,
    canSetPublishedAt,
    canSetPollIds,
    isNew,
    onFormSubmit,
    postType,
    targetSpace,
    fields,
    setEnablePrompt,
    post,
  })

  const defaultValues = useMemo(() => {
    return {
      customSeoDetail: canSetCustomSeoDetail
        ? post?.customSeoDetail
        : undefined,
      tags: post?.tags || [],
      locked: canLock ? post?.locked : undefined,
      publishedAt: canSetPublishedAt ? post?.publishedAt : undefined,
      scheduledFor: post?.scheduledFor,
      owner: canSetOwnerId ? post?.owner?.member || member : undefined,
      ...(canSetSlug ? { slug: post?.slug } : {}),
      fields:
        post?.fields?.reduce((result, cur) => {
          const postTypeField = fields?.find(field => field.key === cur?.key)

          try {
            let value = JSON.parse(cur.value)

            // If it's a relationship replace the value with the entities
            if (
              (postTypeField?.type === CustomFieldType.relation ||
                postTypeField?.items?.type === CustomFieldType.relation) &&
              cur?.relationEntities
            ) {
              const flatRelationEntities = Object.keys(
                cur?.relationEntities,
              ).reduce(
                (result, key) => [
                  ...result,
                  ...(Array.isArray(cur?.relationEntities?.[key])
                    ? cur?.relationEntities?.[key]
                    : []),
                ],
                [],
              )

              if (Array.isArray(value)) {
                value = flatRelationEntities
              } else {
                value = flatRelationEntities?.[0]
              }
            }

            if (postTypeField.type === CustomFieldType.date && value) {
              const subtype = getFieldSetting(postTypeField, 'subtype')
              if (subtype === CustomFieldSubtype.DATETIME) {
                value = convertUtcToTz(value)
              } else {
                value = formatUtcDateTime(value, 'YYYY-MM-DD')
              }
            }

            result[cur.key] = value
          } catch (e) {
            logger.error('Error parsing member field value', e.message)
          }
          return result
        }, {}) || {},
    } satisfies PostFormFields
  }, [
    canSetCustomSeoDetail,
    post?.customSeoDetail,
    post?.tags,
    post?.locked,
    post?.publishedAt,
    post?.scheduledFor,
    post?.owner?.member,
    post?.slug,
    post?.fields,
    canLock,
    canSetPublishedAt,
    canSetOwnerId,
    member,
    canSetSlug,
    fields,
  ])

  useEffect(() => {
    if (formRef.current?.methods && isPostReady && !isFormReady.current) {
      formRef.current.methods.reset(defaultValues)
      isFormReady.current = true
    }
  }, [defaultValues, isPostReady])

  return (
    <Form<PostFormFields>
      ref={formRef}
      defaultValues={defaultValues}
      onChange={() => {
        if (!preview) {
          setEnablePrompt(true)
        }
      }}
      // onSubmit={onSubmit}
    >
      {({ isUploading, setValue }) => {
        return (
          <>
            <Prompt
              when={enablePrompt}
              message={$t({
                id: 'Post.Alert.UnsavedChanges',
                defaultMessage:
                  'You have unsaved changes. Are you sure you want to leave this page?',
              })}
            />
            <Container size="md" direction="vertical" spacing="lg">
              {isNew && availableSpaces?.length > 1 && (
                <Form.SpacePicker
                  label={$t({
                    id: 'Post.PickSpace',
                    defaultMessage: 'Post in',
                  })}
                  onChange={setTargetSpace}
                  name="targetSpace"
                  value={targetSpace}
                  options={availableSpaces}
                />
              )}
              {fields?.map((field, index) => {
                if (
                  field?.archived ||
                  !canUpdateField({ field, entity: post })
                ) {
                  return null
                }

                const placeholder = getFieldSetting(field, 'placeholder')
                const helperText = getFieldSetting(field, 'helperText')
                const isSimilarPostField = field?.key === similarQueryKey

                const similarPosts =
                  decisionSimilarPosts && isSimilarPostField && !post?.id ? (
                    <SimilarPosts
                      query={similarQuery || null}
                      spaceId={targetSpace?.id}
                    />
                  ) : null

                return (
                  <Fragment key={field.key}>
                    <div
                      id={field.key}
                      className={clsx(
                        highlightedField === field?.key &&
                          'ring rounded-sm ring-offset-8 ring-offset-surface ring-customizer-blue relative z-10 bg-surface',
                      )}
                    >
                      <FieldInput
                        name={`fields.${field.key}`}
                        field={field}
                        label={
                          <div className="flex space-s-1 items-center">
                            <span>
                              {getTranslatedPostTypeName(intl, field.name)}
                            </span>
                            <FieldPrivacyBadges
                              field={field}
                              key={field?.key}
                            />
                          </div>
                        }
                        helperText={helperText}
                        placeholder={placeholder}
                        autoFocus={index === 0 && !preview}
                        onChange={
                          isSimilarPostField
                            ? value => setSimilarQuery(value)
                            : null
                        }
                        meta={{
                          composer: {
                            onPollIdsChange: (pollIds: string[]) => {
                              setValue('pollIds', pollIds, {
                                shouldTouch: true,
                              })
                            },
                            poll: {
                              canCreate: canSetPollIds,
                              entityTypeId: post?.id,
                              entityType: PollEntityType.Post,
                              parentHint: !post &&
                                targetSpace && {
                                  entityType: PollEntityType.Space,
                                  entityId: targetSpace?.id,
                                },
                            },
                          },
                        }}
                      />
                    </div>
                    {similarPosts}
                  </Fragment>
                )
              })}
              {postType.context === PostTypeContext.post && !preview && (
                <Form.TagPicker
                  label={$t({
                    id: 'Generics.Tags',
                    defaultMessage: '{TAGS_CC}',
                  })}
                  name="tags"
                  creatable
                  placeholder={$t({
                    id: 'AddTags.Placeholder',
                    defaultMessage: 'Add {TAGS}...',
                  })}
                  multiple
                />
              )}
              {canSetCustomSeoDetail &&
                postType.context === PostTypeContext.post &&
                !preview && (
                  <Accordion variant="plus" iconAlign="trailing">
                    <Accordion.Button>
                      <div className="space-s-1">
                        <span>
                          <T
                            id="Post.SeoOptions"
                            defaultMessage="SEO options"
                          />
                        </span>
                        <AdminOnlyInfo />
                      </div>
                    </Accordion.Button>
                    <Accordion.Panel>
                      <PostSeoOptions space={targetSpace} />
                    </Accordion.Panel>
                  </Accordion>
                )}
              {(canLock || canSetOwnerId || canSetPublishedAt) && !preview && (
                <Accordion
                  variant="plus"
                  iconAlign="trailing"
                  defaultOpen={post?.locked}
                >
                  <Accordion.Button>
                    <div className="space-s-1">
                      <span>
                        <T
                          id="Generics.AdvancedOptions"
                          defaultMessage="Advanced options"
                        />
                      </span>
                      <AdminOnlyInfo />
                    </div>
                  </Accordion.Button>
                  <Accordion.Panel>
                    <Container size="md">
                      {canLock && (
                        <Form.Toggle
                          name="locked"
                          label={$t({
                            id: 'Post.Lock',
                            defaultMessage: 'Lock post',
                          })}
                          helperText={$t({
                            id: 'Post.Lock.Helper',
                            defaultMessage:
                              '{MEMBERS} cannot comment or react to locked posts.',
                          })}
                        />
                      )}
                      {canSetOwnerId && (
                        <Form.MemberPicker
                          name="owner"
                          label={$t({
                            id: 'Generics.Author',
                            defaultMessage: 'Author',
                          })}
                          helperText={$t({
                            id: 'Post.Author.Helper',
                            defaultMessage:
                              'You can post on behalf of another {MEMBER}. Please make sure the author is aware of this.',
                          })}
                        />
                      )}
                      {canSetPublishedAt && (
                        <FormDateTime
                          type="datetime-local"
                          name="publishedAt"
                          dateFormat="Y-m-d H:i"
                          label={$t({
                            id: 'Generics.PublishedOn',
                            defaultMessage: 'Published on',
                          })}
                          helperText={$t({
                            id: 'Post.PublishTime.Helper',
                            defaultMessage:
                              "Use this field to correct or backdate the post's publication date. Future dates are not allowed.",
                          })}
                          maxDate={new Date()}
                        />
                      )}
                    </Container>
                  </Accordion.Panel>
                </Accordion>
              )}

              <Form.Actions>
                <PostFormActions
                  post={post}
                  loading={isAdding || isUpdating || isUploading}
                  disabled={preview}
                  onSubmit={onSubmit}
                  onCancel={onCancel}
                />
              </Form.Actions>
            </Container>
          </>
        )
      }}
    </Form>
  )
}
